import { Checkbox, TextInput } from '@kcftech/design-system';
import { AssetCategoryParameterType } from '@kcftech/prometheustypescriptapiclient';
import {
    getMaxValueRules,
    getMinValueRules,
    getNumericDefaultValueRules,
    MinMaxInputContainer,
} from 'pages/Settings/AssetType/parameter_form/shared';
import { AssetParameterFormInputProps } from 'pages/Settings/AssetType/parameter_form/types';
import { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';

const MinMaxInclusiveContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-top: 1rem;
`;

export function AssetParameterDecimalFormContent({
    form,
    parameter,
}: AssetParameterFormInputProps) {
    const {
        control,
        register,
        formState: { errors },
        watch,
        setValue,
    } = form;

    const { type, minValue, maxValue, minValueInclusive, maxValueInclusive } = watch();

    if (type !== AssetCategoryParameterType.Decimal) {
        throw new Error('Not a valid type');
    }

    const minValueRules = useMemo(() => getMinValueRules(type, maxValue), [
        maxValue,
        type,
    ]);

    const maxValueRules = useMemo(() => getMaxValueRules(type, minValue), [
        minValue,
        type,
    ]);

    const defaultValueRules = useMemo(
        () =>
            getNumericDefaultValueRules({
                type,
                minValue,
                maxValue,
                minValueInclusive,
                maxValueInclusive,
            }),
        [maxValue, maxValueInclusive, minValue, minValueInclusive, type]
    );

    useEffect(() => {
        if (parameter?.CategoryParameterType === AssetCategoryParameterType.Decimal) {
            setValue(
                'minValue',
                parameter.MinValue !== undefined ? `${parameter.MinValue}` : null
            );
            setValue(
                'maxValue',
                parameter.MaxValue !== undefined ? `${parameter.MaxValue}` : null
            );
            setValue('minValueInclusive', parameter.MinValueInclusive ?? true);
            setValue('maxValueInclusive', parameter.MaxValueInclusive ?? true);
            setValue('defaultValue', parameter.DefaultValue ?? '');
        } else {
            setValue('minValue', null);
            setValue('maxValue', null);
            setValue('minValueInclusive', true);
            setValue('maxValueInclusive', true);
            setValue('defaultValue', '');
        }

        return () => {
            setValue('minValue', null);
            setValue('maxValue', null);
            setValue('minValueInclusive', null);
            setValue('maxValueInclusive', null);
            setValue('defaultValue', '');
        };
    }, [
        type,
        setValue,
        parameter?.CategoryParameterType,
        parameter?.DefaultValue,
        parameter?.MinValue,
        parameter?.MaxValue,
        parameter?.MinValueInclusive,
        parameter?.MaxValueInclusive,
    ]);

    return (
        <>
            <div>
                <MinMaxInputContainer>
                    <TextInput
                        label="Min Value"
                        placeholder="Enter a minimum value"
                        error={errors.minValue?.message}
                        width="26rem"
                        {...register('minValue', minValueRules)}
                    />

                    <TextInput
                        label="Max Value"
                        placeholder="Enter a maximum value"
                        error={errors.maxValue?.message}
                        width="26rem"
                        {...register('maxValue', maxValueRules)}
                    />
                </MinMaxInputContainer>

                <MinMaxInclusiveContainer>
                    <Controller
                        control={control}
                        name="minValueInclusive"
                        render={({ field: { value, onChange } }) => (
                            <Checkbox
                                label="Min Inclusive"
                                checked={value ?? false}
                                onChange={onChange}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="maxValueInclusive"
                        render={({ field: { value, onChange } }) => (
                            <Checkbox
                                label="Max Inclusive"
                                checked={value ?? false}
                                onChange={onChange}
                            />
                        )}
                    />
                </MinMaxInclusiveContainer>
            </div>
            <TextInput
                label="Default Value"
                placeholder="Enter a default value"
                error={errors.defaultValue?.message}
                {...register('defaultValue', defaultValueRules)}
            />
        </>
    );
}
